import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useOrders } from '../context/OrderContext';
import { OrderResponse } from '../hooks/useOrders';
import { OrderStatus } from '../components/StatusChips';
import api from '../services/api';
import { Page, PageHead, PageTitle, PageSub } from '../components/ui/Page';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { Callout } from '../components/ui/Callout';
import { Kicker } from '../components/ui/Kicker';
import { Divider } from '../components/ui/Divider';
import { Icon } from '../components/ui/Icon';
import { AlertDialog } from '../components/ui/radix/AlertDialog';
import {
  AddressBlock,
  StatusTimeline,
  ParcelItemsRow,
  buildPaymentStateFromOrder,
  formatOrderDate,
  getOrderStatusValues,
  shipmentLabel,
  shipmentTone,
} from './orderDetailsHelpers';

interface ExtendedParcel {
  id: string;
  width: number;
  height: number;
  length: number;
  weight: number;
  currency: string;
  quantity: number;
  showPrice: boolean;
  physicalWeight: number;
  dimensionalWeight: number;
  dimensionsDisplay: string;
  price: number;
  fedexPackagingType?: string;
  packagingTypeDisplay?: string;
  items?: Array<{
    id: string;
    description: string;
    quantity: number;
    weight: number;
    price: number;
    madeIn: string;
  }>;
}

const OrderDetailsPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [isDownloadingReceipt, setIsDownloadingReceipt] = useState(false);
  const [isDownloadingInvoice, setIsDownloadingInvoice] = useState<null | 'commercial' | 'proforma'>(null);

  const { cancelOrder, isCancelingOrder, downloadLabel, isDownloadingLabel, getOrderById } =
    useOrders();

  const { data, isLoading, isError, error } = useQuery<OrderResponse>({
    queryKey: ['order', id],
    queryFn: () => getOrderById(id || ''),
    enabled: !!id,
    staleTime: 1000 * 60 * 5,
  });

  const handleConfirmCancelOrder = async () => {
    if (!data) return;
    try {
      await cancelOrder(data.id);
      setCancelDialogOpen(false);
    } catch (err) {
      console.error('Failed to cancel order:', err);
    }
  };

  const handleDownloadReceipt = async () => {
    if (!data) return;
    setIsDownloadingReceipt(true);
    try {
      const response = await api.get(`/orders/${data.id}/receipt/download`, {
        responseType: 'blob',
      });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `order-receipt-${data.order_number || data.id}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch {
      alert('Failed to download receipt. Please try again.');
    } finally {
      setIsDownloadingReceipt(false);
    }
  };

  const handleDownloadInvoice = async (type: 'commercial' | 'proforma') => {
    if (!data) return;
    setIsDownloadingInvoice(type);
    try {
      const response = await api.get(`/orders/${data.id}/invoice`, {
        params: { type },
        responseType: 'blob',
      });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${type}-invoice-${data.order_number || data.id}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch {
      alert(`Failed to download ${type} invoice. Please try again.`);
    } finally {
      setIsDownloadingInvoice(null);
    }
  };

  const handleDownloadLabel = async () => {
    if (!data) return;
    const order = data;
    try {
      if (order.label_content) {
        const link = document.createElement('a');
        link.href = `data:application/pdf;base64,${order.label_content}`;
        link.download = `shipping-label-${order.order_number || order.id}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        return;
      }
      const labelData = await downloadLabel(order.id);
      if (labelData?.url) {
        window.open(labelData.url, '_blank');
        return;
      }
      const base64Content = labelData?.base64 || labelData?.content;
      if (base64Content) {
        const link = document.createElement('a');
        link.href = `data:application/pdf;base64,${base64Content}`;
        link.download = `shipping-label-${order.order_number || order.id}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        return;
      }
      throw new Error('No valid label format found in the response');
    } catch {
      alert('Failed to download shipping label. Please try again later.');
    }
  };

  if (isLoading) {
    return (
      <Page width="wide">
        <div className="muted" style={{ textAlign: 'center', padding: 64 }}>
          Loading order details…
        </div>
      </Page>
    );
  }

  if (isError) {
    return (
      <Page width="wide">
        <Callout tone="warn">
          <Icon name="alertCircle" size={16} />
          <span>
            Error loading order details: {error instanceof Error ? error.message : 'Unknown error'}
          </span>
          <button
            type="button"
            className="btn btn-ghost btn-xs"
            onClick={() => navigate('/orders')}
            style={{ marginInlineStart: 'auto' }}
          >
            Back to orders
          </button>
        </Callout>
      </Page>
    );
  }

  if (!data) {
    return (
      <Page width="wide">
        <Callout tone="info">
          <Icon name="info" size={16} />
          <span>No order data found.</span>
        </Callout>
      </Page>
    );
  }

  const order: OrderResponse = data;
  const { orderStatus, paymentStatus, shipmentStatus } = getOrderStatusValues(order);
  const canResumeOrder =
    (orderStatus as OrderStatus) === 'draft' ||
    order.order_status === 'draft' ||
    order.status === 'draft';
  const canCancelOrder = order.order_status
    ? ['draft', 'created', 'processing'].includes(order.order_status)
    : ['pending', 'processing'].includes(order.status);

  const handleResumeOrder = () => {
    navigate('/shipping-payment', { state: buildPaymentStateFromOrder(order) });
  };

  const fromCity =
    order.from_address?.city?.name || (order.from_address as any)?.city_other || '';
  const toCity =
    order.to_address?.city?.name || (order.to_address as any)?.city_other || '';

  return (
    <Page width="wide">
      <Button
        variant="ghost"
        size="sm"
        onClick={() => navigate('/orders')}
        style={{ marginBottom: 16 }}
      >
        <Icon name="arrowLeft" size={14} />
        <span>Back to shipments</span>
      </Button>

      <PageHead>
        <div>
          <Kicker>Shipment · {order.order_number || order.id}</Kicker>
          <PageTitle style={{ marginTop: 8 }}>
            {fromCity} → {toCity}
          </PageTitle>
          <PageSub>
            Created {formatOrderDate(order.created_at)} ·{' '}
            {order.service || order.selected_rate?.name || 'Service'}
          </PageSub>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {canResumeOrder && (
            <Button variant="primary" size="sm" onClick={handleResumeOrder}>
              <Icon name="checkCircle" size={14} />
              <span>Complete order</span>
            </Button>
          )}
          <Button size="sm" onClick={handleDownloadReceipt} disabled={isDownloadingReceipt}>
            <Icon name="receipt" size={14} />
            <span>{isDownloadingReceipt ? 'Downloading…' : 'Receipt'}</span>
          </Button>
          <Button
            size="sm"
            onClick={() => handleDownloadInvoice('commercial')}
            disabled={isDownloadingInvoice !== null}
            title="Download commercial invoice PDF"
          >
            <Icon name="download" size={14} />
            <span>
              {isDownloadingInvoice === 'commercial' ? 'Downloading…' : 'Commercial Invoice'}
            </span>
          </Button>
          <Button
            size="sm"
            onClick={() => handleDownloadInvoice('proforma')}
            disabled={isDownloadingInvoice !== null}
            title="Download proforma invoice PDF"
          >
            <Icon name="download" size={14} />
            <span>
              {isDownloadingInvoice === 'proforma' ? 'Downloading…' : 'Proforma Invoice'}
            </span>
          </Button>
          <Button
            size="sm"
            onClick={handleDownloadLabel}
            disabled={isDownloadingLabel || (!order.label_url && !order.label_content)}
            title={
              order.label_content || order.label_url
                ? 'Download shipping label PDF'
                : 'No shipping label available'
            }
          >
            <Icon name="download" size={14} />
            <span>{isDownloadingLabel ? 'Downloading…' : 'Label'}</span>
          </Button>
          {order.tracking_number && (
            <Button
              variant="primary"
              size="sm"
              onClick={() => {
                const provider = order.carrier || order.provider;
                const url = provider
                  ? `/track/${order.tracking_number}?provider=${provider.toLowerCase()}`
                  : `/track/${order.tracking_number}`;
                navigate(url);
              }}
            >
              <Icon name="route" size={14} />
              <span>Track</span>
            </Button>
          )}
          {canCancelOrder && (
            <Button
              size="sm"
              variant="danger"
              onClick={() => setCancelDialogOpen(true)}
              disabled={isCancelingOrder}
            >
              <Icon name="x" size={14} />
              <span>Cancel</span>
            </Button>
          )}
        </div>
      </PageHead>

      <div
        style={{ display: 'grid', gridTemplateColumns: '1.6fr 1fr', gap: 20 }}
        className="details-grid"
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          <Card pad>
            <div
              className="row"
              style={{
                justifyContent: 'space-between',
                marginBottom: 16,
                gap: 12,
                flexWrap: 'wrap',
              }}
            >
              <h3 style={{ fontSize: 15 }}>Status</h3>
              <Badge tone={shipmentTone(shipmentStatus)} dot>
                {shipmentLabel(shipmentStatus)}
              </Badge>
            </div>
            <StatusTimeline shipmentStatus={shipmentStatus} />
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)',
                gap: 14,
                marginTop: 20,
              }}
            >
              {[
                ['ORDER STATUS', orderStatus.replace(/_/g, ' ')],
                ['PAYMENT', paymentStatus.replace(/_/g, ' ')],
                ['SHIPMENT', shipmentLabel(shipmentStatus)],
              ].map(([label, value]) => (
                <div key={label}>
                  <div
                    className="mono"
                    style={{ fontSize: 11, color: 'var(--ink-4)', letterSpacing: '0.06em' }}
                  >
                    {label}
                  </div>
                  <div style={{ fontSize: 14, fontWeight: 500, marginTop: 2 }}>{value}</div>
                </div>
              ))}
            </div>
          </Card>

          <div className="grid-2">
            <AddressBlock title="From" address={order.from_address} />
            <AddressBlock title="To" address={order.to_address} />
          </div>

          <Card pad>
            <h3 style={{ fontSize: 15, marginBottom: 14 }}>Parcels & contents</h3>
            <table className="table">
              <thead>
                <tr>
                  <th>Dimensions</th>
                  <th>Weight</th>
                  <th>Qty</th>
                  <th>Description</th>
                  <th style={{ textAlign: 'right' }}>Value</th>
                </tr>
              </thead>
              <tbody>
                {order.parcels.map((parcel, idx) => {
                  const ext = parcel as unknown as ExtendedParcel;
                  const rawItems =
                    ext.items ||
                    (parcel as any).Items ||
                    (parcel as any).parcel_items ||
                    [];
                  const itemList = Array.isArray(rawItems)
                    ? rawItems
                    : typeof rawItems === 'string'
                      ? (() => {
                          try {
                            return JSON.parse(rawItems);
                          } catch {
                            return [];
                          }
                        })()
                      : [];
                  return (
                    <React.Fragment key={ext.id || `parcel-${idx}`}>
                      <tr>
                        <td className="mono" style={{ fontSize: 13 }}>
                          {ext.dimensionsDisplay ||
                            `${ext.length} × ${ext.width} × ${ext.height} cm`}
                        </td>
                        <td className="mono" style={{ fontSize: 13 }}>
                          {ext.weight} kg
                        </td>
                        <td className="mono">{ext.quantity}</td>
                        <td>
                          {ext.fedexPackagingType || ext.packagingTypeDisplay || 'Package'}
                        </td>
                        <td
                          className="mono"
                          style={{ textAlign: 'right', fontWeight: 600 }}
                        >
                          {ext.showPrice && ext.price > 0
                            ? `${ext.price} ${ext.currency || order.currency}`
                            : 'N/A'}
                        </td>
                      </tr>
                      <ParcelItemsRow
                        items={itemList}
                        currency={ext.currency || order.currency || 'ILS'}
                      />
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </Card>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          <Card pad>
            <div
              style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink-2)', marginBottom: 8 }}
            >
              TOTAL {paymentStatus === 'paid' ? 'PAID' : 'AMOUNT'}
            </div>
            <div
              className="mono"
              style={{ fontSize: 32, fontWeight: 600, letterSpacing: '-0.02em' }}
            >
              {order.total_price} {order.currency}
            </div>
            <div className="muted" style={{ fontSize: 13 }}>
              {order.paid_at ? `Paid ${formatOrderDate(order.paid_at)}` : 'Awaiting payment'}
            </div>
            <div
              style={{
                marginTop: 14,
                paddingTop: 14,
                borderTop: '1px solid var(--line)',
              }}
            >
              {[
                [
                  'Shipping',
                  order.payment_details?.shipping ??
                    (order as any).shipping_cost ??
                    order.total_price ??
                    0,
                ],
                ['Insurance', order.payment_details?.insurance ?? 0],
                ['Tax', order.payment_details?.tax ?? 0],
              ].map(([label, value]) => (
                <div
                  key={label as string}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    padding: '4px 0',
                    fontSize: 13,
                  }}
                >
                  <span className="muted">{label}</span>
                  <span className="mono">
                    {value} {order.currency}
                  </span>
                </div>
              ))}
            </div>
            <Divider style={{ margin: '12px 0' }} />
            <div
              style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}
            >
              <span className="muted">Method</span>
              <span style={{ textTransform: 'capitalize' }}>{order.payment_method || '—'}</span>
            </div>
          </Card>

          <Card pad>
            <h3 style={{ fontSize: 15, marginBottom: 12 }}>Shipment details</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {[
                ['Service', order.service || order.selected_rate?.name || 'N/A'],
                ['Carrier', order.carrier || order.provider || '—'],
                ['Tracking', order.tracking_number || 'N/A'],
                ['Reference', order.reference_number || 'N/A'],
                ['Order date', formatOrderDate(order.created_at)],
                ['Shipped', formatOrderDate(order.shipped_at)],
                ['ETA', order.delivery_estimate || 'N/A'],
              ].map(([label, value]) => (
                <div
                  key={label as string}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    fontSize: 13,
                  }}
                >
                  <span className="muted">{label}</span>
                  <span className="mono" style={{ textAlign: 'right' }}>
                    {value}
                  </span>
                </div>
              ))}
            </div>
          </Card>

          {(order.label_content || order.label_url) && (
            <Card pad>
              <Callout tone="ok">
                <Icon name="checkCircle" size={16} />
                <div>
                  <strong>Shipping label ready.</strong>
                  <div style={{ marginTop: 4, fontSize: 13 }}>
                    Click the Label button above to download as PDF.
                  </div>
                </div>
              </Callout>
            </Card>
          )}
        </div>
      </div>

      <AlertDialog
        open={cancelDialogOpen}
        onOpenChange={setCancelDialogOpen}
        title="Cancel order"
        description={
          order
            ? `Cancel order #${order.order_number || order.id}? This action cannot be undone.${
                order.payment_status === 'paid'
                  ? ' A refund will be processed according to our refund policy.'
                  : ''
              }`
            : ''
        }
        confirmLabel={isCancelingOrder ? 'Cancelling…' : 'Cancel order'}
        cancelLabel="Keep order"
        onConfirm={handleConfirmCancelOrder}
        destructive
      />
    </Page>
  );
};

export default OrderDetailsPage;
